import React, {useCallback, useMemo, useState} from 'react';
import {FlatList, Pressable, RefreshControl, StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchMembers, removeMember} from '@/entities/member/api';
import type {Member} from '@/entities/member/types';
import {assignRole, bulkAssignRole, bulkRevokeRole, revokeRole} from '@/entities/role/api';
import {useAuthStore} from '@/entities/session/auth-store';
import {MemberAccessSheet} from '@/features/manage-member-access/ui/MemberAccessSheet';
import {useCompanyRoles} from '@/features/manage-members/lib/company-roles';
import {Avatar} from '@/shared/ui/avatar';
import {Badge, type BadgeVariant} from '@/shared/ui/badge';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {Pager} from '@/shared/ui/pager';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {useClientPage} from '@/shared/lib/use-client-page';
import {Icon} from '@/shared/ui/icons';
import {ChoiceChips} from '@/shared/ui/segmented';
import {AppInput} from '@/shared/ui/input';
import {FilterSelect} from '@/shared/ui/filter-bar';
import {useTableSort, type SortAccessors} from '@/shared/lib/table-sort';
import {SkeletonList} from '@/shared/ui/skeleton';
import {AddMemberSheet} from './AddMemberSheet';
import {UnlistedRoleHolders} from './UnlistedRoleHolders';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/** Строк в странице списка участников, и что предлагает рейка под ним. */
const PAGE_SIZE = 20;
const PAGE_SIZES = [20, 50, 100] as const;

/**
 * Who can administer this company.
 *
 * Roles used to be read-only here on the belief that the gateway had no owner-facing
 * role-assignment route. It does — `POST /roles/assign` and `/roles/revoke`, plus an
 * optional `role` on the add call — so a role is now a control, not a badge. Without
 * it a company had exactly one person who could ever administer it, and no way to
 * appoint a second from the cabinet.
 *
 * Two writes are still refused locally, for the same reasons as before: never change
 * or remove YOURSELF (an admin locking themselves out) and never the OWNER (orphaning
 * the company). Everything else is left to Casbin, which answers 403 if it disagrees.
 *
 * ## Three things changed here after the API surface was walked end to end
 *
 * **The role chips are the server's list now**, not two codes typed into this file —
 * `useCompanyRoles` reads `GET /roles/definitions`, which is localized and grows
 * without a release. See that module for why the old constant survives as a fallback.
 *
 * **The calls moved to `entities/role/api.ts`, and gained a header.** `/roles/*` takes
 * its Casbin domain from `x-company-id`; sent from here through the plain `request()`
 * they were being authorized in `system`, where a company owner holds nothing. Every
 * chip on this screen answered 403 for exactly the person it was built for.
 *
 * **Selection.** A role change is one call per person, and a company onboarding a
 * shift of eight accountants was eight round trips of chip-tapping. `POST
 * /roles/bulk/{assign,revoke}` takes up to 500 ids; the selection bar below is that
 * route. It reports `failed[]` rather than saying «готово» on a resolved promise —
 * these two routes answer 500 for ordinary input errors, so a silent success would be
 * a lie often enough to matter.
 *
 * Remove is a two-step: the quiet ghost button arms it, and only then does the red one
 * appear, on its own line under the member. The old layout stacked both in the row's
 * right-hand column, where a 46pt destructive button sat a thumb-width from the row you
 * were merely reading.
 */
const styles = StyleSheet.create({
  list: {gap: spacing(2), paddingVertical: spacing(3), flexGrow: 1},
  pager: {paddingTop: spacing(3)},
  listEmpty: {justifyContent: 'center'},
  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  body: {flex: 1, gap: 2},
  roles: {marginTop: spacing(3)},
  header: {gap: spacing(3)},
  /* Полоса над списком — как у всех таблиц кабинета: слева то, чем сужают, справа
     то, что делают. На узком окне переносится: поиск остаётся один на строке,
     глаголы уходят под него и прижимаются к правому краю. */
  toolbar: {flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: spacing(2)},
  toolbarSearch: {flexGrow: 1, flexBasis: 240, minWidth: 200},
  toolbarActions: {flexDirection: 'row', alignItems: 'center', gap: spacing(2), marginLeft: 'auto'},
  confirm: {marginTop: spacing(3), gap: spacing(2)},
  confirmRow: {flexDirection: 'row', gap: spacing(2)},
  confirmButton: {flex: 1},
  check: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bulk: {gap: spacing(3)},
  bulkRow: {flexDirection: 'row', gap: spacing(2), flexWrap: 'wrap'},
  bulkButton: {flexGrow: 1, flexBasis: 130},
});

/**
 * A role is a BADGE, not a status pill.
 *
 * It used to be a `StatusPill`: owner wore `granted` and admin wore `pending`, which
 * put the online-green and the attention-amber on a person's job title — three columns
 * from a list where those exact colours mean something else entirely. The console's
 * rule is that a colour means identity or it means state, never both, and a role is
 * neither: it is a LABEL, so it takes the neutral badge family and its weight comes
 * from the tier, not from a hue that means "healthy".
 */
function roleVariant(roles: string[]): BadgeVariant {
  if (roles.includes('owner')) {
    return 'info';
  }
  return roles.length > 0 ? 'secondary' : 'muted';
}

function MemberRow({
  member,
  removable,
  onRemove,
  removing,
  onChangeRole,
  changingRole,
  roleOptions,
  roleLabelFor,
  onOpenAccess,
  selecting,
  selected,
  onToggleSelect,
}: {
  member: Member;
  removable: boolean;
  onRemove: () => void;
  removing: boolean;
  onChangeRole: (next: string) => void;
  changingRole: boolean;
  roleOptions: {code: string; label: string}[];
  roleLabelFor: (code: string) => string;
  onOpenAccess: () => void;
  selecting: boolean;
  selected: boolean;
  onToggleSelect: () => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const [confirm, setConfirm] = useState(false);
  const name = member.full_name || member.email || member.username;

  // Which of the OFFERED roles this member currently holds, or '' for none. Only the
  // offered ones: a member holding a role the catalogue no longer assigns still shows
  // it in the badge, but the chips must not silently propose to drop it.
  const current =
    roleOptions.find(o => member.roles.includes(o.code))?.code ?? '';
  const badgeLabel = member.roles.length
    ? roleLabelFor(member.roles[0])
    : t('members.role.member');

  return (
    <Card padding={3}>
      <View style={styles.row}>
        {selecting ? (
          <Pressable
            onPress={onToggleSelect}
            accessibilityRole="checkbox"
            accessibilityState={{checked: selected}}
            testID={`member-select-${member.id}`}
            style={[
              styles.check,
              {
                borderColor: selected ? p.primary : p.borderStrong,
                backgroundColor: selected ? p.primary : 'transparent',
              },
            ]}>
            {selected ? (
              <Icon name="check" size={14} color={p.primaryForeground} />
            ) : null}
          </Pressable>
        ) : (
          <Avatar name={name} />
        )}
        <View style={styles.body}>
          <AppText variant="subtitle" numberOfLines={1}>
            {name}
          </AppText>
          {member.email ? (
            <AppText variant="caption" numberOfLines={1}>
              {member.email}
            </AppText>
          ) : null}
        </View>
        <Badge variant={roleVariant(member.roles)} size="sm" label={badgeLabel} />
        {!selecting ? (
          <AppButton
            title={t('members.access')}
            tone="ghost"
            size="sm"
            onPress={onOpenAccess}
            testID={`member-access-${member.id}`}
          />
        ) : null}
        {removable && !selecting && !confirm ? (
          <AppButton
            title={t('members.remove')}
            tone="ghost"
            size="sm"
            onPress={() => setConfirm(true)}
          />
        ) : null}
      </View>

      {/* Role is editable on exactly the members that are removable — the same two
          exclusions (self, owner) protect against the same two mistakes. */}
      {removable && !selecting ? (
        <View style={styles.roles}>
          <ChoiceChips
            value={current}
            onChange={onChangeRole}
            disabled={changingRole}
            testID={`member-role-${member.id}`}
            options={[
              {value: '', label: t('members.role.member')},
              ...roleOptions.map(o => ({value: o.code, label: o.label})),
            ]}
          />
        </View>
      ) : null}

      {removable && confirm ? (
        <View style={styles.confirm}>
          <View style={styles.confirmRow}>
            <AppButton
              title={t('actions.cancel')}
              tone="neutral"
              size="sm"
              onPress={() => setConfirm(false)}
              style={styles.confirmButton}
            />
            <AppButton
              title={t('members.confirmRemove')}
              tone="destructive"
              size="sm"
              onPress={onRemove}
              loading={removing}
              style={styles.confirmButton}
            />
          </View>
        </View>
      ) : null}
    </Card>
  );
}

export function MemberList({companyId}: {companyId: string}) {
  const {t} = useTranslation();
  const p = useTheme();
  const {isDesktop} = useBreakpoint();
  const qc = useQueryClient();
  const ownAccountId = useAuthStore(s => s.account?.id);
  const roles = useCompanyRoles();

  const query = useQuery({
    queryKey: ['members', companyId],
    queryFn: () => fetchMembers(companyId, {perPage: 100}),
  });

  const [adding, setAdding] = useState(false);
  const [accessFor, setAccessFor] = useState<Member | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  const remove = useMutation({
    meta: {toast: 'toast.memberRemoved'},
    mutationFn: (memberId: string) => removeMember(companyId, memberId),
    onSuccess: () => qc.invalidateQueries({queryKey: ['members', companyId]}),
  });

  /**
   * Changing a role is revoke-then-assign, in that order: the two routes each act on
   * ONE role code, so moving someone from `accountant` to `admin` without revoking
   * first would leave them holding both. Revoking what they do not have is harmless,
   * which is why the revoke is unconditional.
   */
  const changeRole = useMutation({
    mutationFn: async (input: {member: Member; next: string}) => {
      const offered = roles.options.map(o => o.code);
      for (const code of input.member.roles) {
        if (offered.includes(code) && code !== input.next) {
          await revokeRole(companyId, input.member.account_id, code);
        }
      }
      if (input.next) {
        await assignRole(companyId, input.member.account_id, input.next);
      }
    },
    onSuccess: () => qc.invalidateQueries({queryKey: ['members', companyId]}),
  });

  // Memoised because two other memos depend on it: `query.data?.data ?? []` is a fresh
  // array on every render, which would make the fallback the only thing that ever
  // invalidates them.
  const items = useMemo(() => query.data?.data ?? [], [query.data]);

  /*
    Отбор и порядок — по всем участникам, а не по видимой странице: список приезжает
    одной страницей в сто штук, листает клиент. Поэтому «только администраторы»
    означает всех администраторов компании.
  */
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const needle = search.trim().toLowerCase();
  const matched = useMemo(
    () =>
      items.filter(m => {
        if (role && !m.roles.includes(role)) {
          return false;
        }
        if (!needle) {
          return true;
        }
        return [m.full_name, m.email, m.username, m.position]
          .filter(Boolean)
          .some(field => field.toLowerCase().includes(needle));
      }),
    [items, role, needle],
  );

  const ACCESSORS: SortAccessors<Member> = {
    name: m => m.full_name,
    email: m => m.email,
    position: m => m.position,
  };
  const sorted = useTableSort(matched, ACCESSORS, {key: 'name', direction: 'asc'});

  // Участники приезжают одной страницей в сто штук — листает клиент, и рейка под
  // списком это не скрывает.
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const paged = useClientPage(sorted.rows, pageSize);

  /** Only the members a role may be changed on — self and the owner are never in it. */
  const selectable = useMemo(
    () =>
      items.filter(
        m => m.account_id !== ownAccountId && !m.roles.includes('owner'),
      ),
    [items, ownAccountId],
  );

  const bulk = useMutation({
    meta: {toast: 'toast.bulkDone'},
    mutationFn: (input: {roleCode: string; revoke: boolean}) => {
      const accountIds = selectable
        .filter(m => selected.includes(m.id))
        .map(m => m.account_id);
      return input.revoke
        ? bulkRevokeRole(companyId, accountIds, input.roleCode)
        : bulkAssignRole(companyId, accountIds, input.roleCode);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({queryKey: ['members', companyId]});
    },
  });

  /**
   * The roles worth cross-checking against the member list: everything this screen can
   * grant, plus `owner` — which it deliberately cannot grant and most needs to see.
   */
  const auditedRoles = useMemo(
    () => ['owner', ...roles.options.map(o => o.code)],
    [roles.options],
  );

  const leaveSelection = () => {
    setSelecting(false);
    setSelected([]);
    bulk.reset();
  };

  const renderItem = useCallback(
    ({item}: {item: Member}) => {
      // Never removable: yourself (would lock you out) or the owner (orphans the company).
      const removable =
        item.account_id !== ownAccountId && !item.roles.includes('owner');
      return (
        <MemberRow
          member={item}
          removable={removable}
          removing={remove.isPending && remove.variables === item.id}
          onRemove={() => remove.mutate(item.id)}
          changingRole={
            changeRole.isPending && changeRole.variables?.member.id === item.id
          }
          onChangeRole={next => changeRole.mutate({member: item, next})}
          roleOptions={roles.options}
          roleLabelFor={roles.labelFor}
          onOpenAccess={() => setAccessFor(item)}
          selecting={selecting && removable}
          selected={selected.includes(item.id)}
          onToggleSelect={() =>
            setSelected(prev =>
              prev.includes(item.id)
                ? prev.filter(id => id !== item.id)
                : [...prev, item.id],
            )
          }
        />
      );
    },
    [ownAccountId, remove, changeRole, roles, selecting, selected],
  );

  if (query.isError) {
    return (
      <EmptyState
        tone="error"
        title={t('members.error')}
        actionLabel={t('actions.retry')}
        onAction={() => {
          void query.refetch();
        }}
      />
    );
  }

  if (query.isPending) {
    return (
      <View style={styles.list}>
        <SkeletonList count={5} />
      </View>
    );
  }

  /** Роли, которые ВСТРЕЧАЮТСЯ в списке, плюс «владелец» — его выдать нельзя, а
   *  найти нужно чаще прочих. */
  const roleOptions = [
    {value: '', label: t('actions.any')},
    ...auditedRoles
      .filter(code => items.some(m => m.roles.includes(code)))
      .map(code => ({
        value: code,
        label: roles.options.find(o => o.code === code)?.label ?? code,
      })),
  ];

  const header = (
    <View style={styles.header}>
      <View style={styles.toolbar}>
        <AppInput
          value={search}
          onChangeText={setSearch}
          placeholder={t('members.search')}
          icon="search"
          size={isDesktop ? 'md' : 'lg'}
          autoCapitalize="none"
          autoCorrect={false}
          style={styles.toolbarSearch}
          testID="member-search"
        />
        {roleOptions.length > 1 ? (
          <FilterSelect
            label={t('members.roleFilter')}
            value={role}
            options={roleOptions}
            onChange={setRole}
            phone={!isDesktop}
            testID="member-role-filter"
          />
        ) : null}
        <View style={styles.toolbarActions}>
          {selectable.length > 1 ? (
            <AppButton
              title={selecting ? t('actions.cancel') : t('members.select')}
              tone="outline"
              size={isDesktop ? 'md' : 'lg'}
              onPress={() => (selecting ? leaveSelection() : setSelecting(true))}
              testID="member-select-mode"
            />
          ) : null}
          <AppButton
            title={t('members.add')}
            icon="plus"
            size={isDesktop ? 'md' : 'lg'}
            onPress={() => setAdding(true)}
            disabled={selecting}
            testID="member-add"
          />
        </View>
      </View>

      {selecting ? (
        <Card padding={4} style={styles.bulk}>
          <AppText variant="caption">
            {t('members.selected', {count: selected.length})}
          </AppText>

          {/* One role per call — the routes take a single `role_code` for the whole
              batch — so this is a chip row, not a picker plus an apply button. */}
          {roles.options.map(option => (
            <View key={option.code} style={styles.bulkRow}>
              <AppText variant="label" style={styles.bulkButton}>
                {option.label}
              </AppText>
              <AppButton
                title={t('members.bulkAssign')}
                tone="soft"
                size="sm"
                disabled={selected.length === 0 || bulk.isPending}
                onPress={() => bulk.mutate({roleCode: option.code, revoke: false})}
                style={styles.bulkButton}
                testID={`member-bulk-assign-${option.code}`}
              />
              <AppButton
                title={t('members.bulkRevoke')}
                tone="neutral"
                size="sm"
                disabled={selected.length === 0 || bulk.isPending}
                onPress={() => bulk.mutate({roleCode: option.code, revoke: true})}
                style={styles.bulkButton}
                testID={`member-bulk-revoke-${option.code}`}
              />
            </View>
          ))}

          {/* Never «готово» on a resolved promise: these routes answer 200 with a
              per-item outcome, and 500 for a plain input error. */}
          {bulk.isError ? (
            <Banner tone="error" title={(bulk.error as Error)?.message ?? t('errors.generic')} />
          ) : null}
          {bulk.isSuccess && bulk.data ? (
            <Banner
              tone={bulk.data.failed.length > 0 ? 'warning' : 'success'}
              title={t('bulk.outcome', {
                ok: bulk.data.succeeded.length,
                failed: bulk.data.failed.length,
              })}
              message={
                bulk.data.failed.length > 0
                  ? bulk.data.failed
                      .slice(0, 3)
                      .map(f => `${f.id}: ${f.error}`)
                      .join('\n')
                  : undefined
              }
            />
          ) : null}
        </Card>
      ) : null}

      {roles.isFallback ? (
        <Banner tone="warning" title={t('members.catalogueOffline')} />
      ) : null}

      {/* Membership and authority live in two services, and removing a member does not
          revoke their Casbin role. This is the difference between the two lists. */}
      <UnlistedRoleHolders
        companyId={companyId}
        members={items}
        roleCodes={auditedRoles}
      />
    </View>
  );

  return (
    <>
      <FlatList
        testID="member-list"
        ListHeaderComponent={header}
        data={paged.rows}
        keyExtractor={item => item.id}
        renderItem={renderItem}
        showsVerticalScrollIndicator
        contentContainerStyle={[styles.list, items.length === 0 && styles.listEmpty]}
        ListEmptyComponent={<EmptyState icon="people" title={t('members.empty')} />}
        ListFooterComponent={
          items.length > 0 ? (
            <Pager
              page={paged.page}
              totalPages={paged.totalPages}
              total={paged.total}
              perPage={paged.perPage}
              onPage={paged.setPage}
              perPageOptions={PAGE_SIZES}
              onPerPage={setPageSize}
              style={styles.pager}
              testID="member-pager"
            />
          ) : null
        }
        refreshControl={
          <RefreshControl
            refreshing={query.isRefetching}
            onRefresh={() => {
              void query.refetch();
            }}
            tintColor={p.primary}
            colors={[p.primary]}
          />
        }
      />
      <AddMemberSheet
        companyId={companyId}
        visible={adding}
        onClose={() => setAdding(false)}
      />
      <MemberAccessSheet
        companyId={companyId}
        member={accessFor}
        visible={accessFor !== null}
        onClose={() => setAccessFor(null)}
      />
    </>
  );
}
